/**
 * Speakable text
 *
 * Turns assistant chat content into something a TTS engine can read aloud
 * without spelling bracket markers, JSON payloads, or markdown chrome.
 *
 * Primary responsibilities:
 * - Strip / humanize [USER_QUESTIONNAIRE…] and [SUGGESTIONS…]
 * - Drop other interactive [TAG]…[/TAG] / fence markers
 * - Keep the spoken question + option labels when a questionnaire was present
 */

import { parseSuggestions } from '@/lib/utils/parse-suggestions'
import type {
  UserQuestionnaire,
  UserQuestionnaireQuestion
} from '@/types/user-questionnaire'

const CODE_FENCE_PATTERN = /```[\s\S]*?```/g
const HTML_CARD_FENCE_PATTERN = /```html-card[\s\S]*?```/gi
const PAIRED_BRACKET_MARKER_PATTERN =
  /\\?\[\s*([A-Z][A-Z0-9_]*)\b[^\]]*][\s\S]*?\[\/\s*\1\s*]/gi
const SINGLE_BRACKET_MARKER_PATTERN =
  /\\?\[\s*(?:TREND|LOSI_PLANNER_DAY|LOSI_APP_PREVIEW|SPACE_ITEM_CARD|PROGRESS|YOUTUBE_VIDEO(?:_LIST)?|TODO|CHECKLIST|FLASHCARD|QUIZ|POLL|TIMELINE|RATING|TABS|DEFINITION|FORM|VISUAL_ARTIFACT|CODE_DIFF|CODE_SNIPPET|STORYBOARD|VIDEO_TIMESTAMP|GET_PREMIUM|MIND_MAP|FLOW_CHART|ARGUMENT_MAP|GANTT|DASHBOARD|QUOTE)\b[^\]]*]/gi
const PARTIAL_MARKER_TAIL_PATTERN =
  /\\?\[\s*(?:SUGGESTIONS|USER[_\s-]*QUESTIONN?AIRE|[A-Z][A-Z0-9_]*)[\s\S]*$/i

function joinSpokenList(items: string[]) {
  const cleaned = items.map(item => item.trim()).filter(Boolean)
  if (cleaned.length === 0) return ''
  if (cleaned.length === 1) return cleaned[0]
  if (cleaned.length === 2) return `${cleaned[0]} or ${cleaned[1]}`
  return `${cleaned.slice(0, -1).join(', ')}, or ${cleaned[cleaned.length - 1]}`
}

function speakQuestion(question: UserQuestionnaireQuestion): string {
  const prompt = question.prompt?.trim() || ''
  const labels = (question.options || [])
    .map(option => option.label?.trim() || '')
    .filter(Boolean)

  if (question.mode === 'approval') {
    return prompt || 'Do you approve?'
  }

  if (question.mode === 'yes_no') {
    return prompt || 'Yes or no?'
  }

  const optionsSpoken = joinSpokenList(labels)
  if (!prompt) return optionsSpoken
  if (!optionsSpoken) return prompt

  const trimmedPrompt = prompt.replace(/[.?!\s]+$/u, '')
  return `${trimmedPrompt}: ${optionsSpoken}?`
}

function speakQuestionnaire(questionnaire: UserQuestionnaire): string {
  const questions =
    questionnaire.questions && questionnaire.questions.length > 0
      ? questionnaire.questions
      : [questionnaire]

  return questions.map(speakQuestion).filter(Boolean).join(' ')
}

function speakSuggestions(suggestions: string[]): string {
  const list = joinSpokenList(suggestions)
  return list ? `You could pick ${list}.` : ''
}

function stripResidualMarkers(text: string) {
  return text
    .replace(HTML_CARD_FENCE_PATTERN, ' ')
    .replace(CODE_FENCE_PATTERN, ' see code example in the chat ')
    .replace(PAIRED_BRACKET_MARKER_PATTERN, ' ')
    .replace(SINGLE_BRACKET_MARKER_PATTERN, ' ')
    .replace(PARTIAL_MARKER_TAIL_PATTERN, ' ')
}

function splitTableRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  return trimmed.split('|').map(cell => cell.trim())
}

function isTableSeparatorRow(cells: string[]) {
  return (
    cells.length > 0 &&
    cells.every(cell => /^:?-{3,}:?$/.test(cell.replace(/\s+/g, '')))
  )
}

function isTableRowLine(line: string) {
  const trimmed = line.trim()
  if (!trimmed.includes('|')) return false
  // Require a real pipe cell boundary, not a lone prose "|".
  return /\|/.test(trimmed) && splitTableRow(trimmed).length >= 2
}

/** Turn GFM pipe tables into "Header is value" sentences — never speak "|". */
function humanizeMarkdownTables(text: string) {
  const lines = text.split(/\r?\n/)
  const out: string[] = []
  let i = 0

  while (i < lines.length) {
    if (
      isTableRowLine(lines[i] || '') &&
      i + 1 < lines.length &&
      isTableSeparatorRow(splitTableRow(lines[i + 1] || ''))
    ) {
      const headers = splitTableRow(lines[i] || '')
      i += 2 // skip header + separator
      const rowSentences: string[] = []

      while (i < lines.length && isTableRowLine(lines[i] || '')) {
        const cells = splitTableRow(lines[i] || '')
        i += 1
        if (isTableSeparatorRow(cells)) continue

        const pairs = headers
          .map((header, index) => {
            const value = (cells[index] || '').trim()
            const label = header.trim()
            if (!label && !value) return ''
            if (!label) return value
            if (!value) return ''
            return `${label} is ${value}`
          })
          .filter(Boolean)

        if (pairs.length > 0) {
          rowSentences.push(`${pairs.join(', ')}.`)
        }
      }

      if (rowSentences.length > 0) {
        out.push(rowSentences.join(' '))
      }
      continue
    }

    // Stray pipe-only lines (no header/separator) — speak cells as a list.
    if (isTableRowLine(lines[i] || '')) {
      const cells = splitTableRow(lines[i] || '').filter(
        cell => cell && !/^:?-{3,}:?$/.test(cell.replace(/\s+/g, ''))
      )
      if (cells.length > 0) {
        out.push(`${cells.join(', ')}.`)
      }
      i += 1
      continue
    }

    out.push(lines[i] || '')
    i += 1
  }

  return out.join('\n')
}

function softenMarkdown(text: string) {
  return text
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)]\([^)]*\)/g, '$1')
    .replace(/[*_~`#]+/g, '')
    // Safety net: never let a leftover pipe reach TTS ("vertical bar").
    .replace(/\|+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Convert assistant (or any) message content into natural spoken prose.
 * Safe for empty / partial streaming tails.
 */
export function toSpeakableText(raw: string | null | undefined): string {
  const source = typeof raw === 'string' ? raw : ''
  if (!source.trim()) return ''

  const parsed = parseSuggestions(source)
  const spokenControls = [
    parsed.questionnaire ? speakQuestionnaire(parsed.questionnaire) : '',
    parsed.suggestions.length > 0 ? speakSuggestions(parsed.suggestions) : ''
  ]
    .filter(Boolean)
    .join(' ')

  const prose = softenMarkdown(
    humanizeMarkdownTables(stripResidualMarkers(parsed.content))
  )
  if (!prose) return spokenControls
  if (!spokenControls) return prose

  // Avoid repeating the same question if prose already ends with it.
  if (prose.toLowerCase().includes(spokenControls.toLowerCase().slice(0, 24))) {
    return prose
  }

  return `${prose} ${spokenControls}`.replace(/\s+/g, ' ').trim()
}

/**
 * Streaming-safe flush for LiveKit `ttsNode`: speak complete prose, hold back
 * an incomplete marker suffix until the closing arrives (or the stream ends).
 */
export function flushSpeakableBuffer(buffer: string): {
  speakable: string
  rest: string
} {
  if (!buffer) return { speakable: '', rest: '' }

  const partial = buffer.match(PARTIAL_MARKER_TAIL_PATTERN)
  if (partial?.index != null) {
    const complete = buffer.slice(0, partial.index)
    const rest = buffer.slice(partial.index)
    return {
      speakable: complete ? toSpeakableText(complete) : '',
      rest
    }
  }

  return { speakable: toSpeakableText(buffer), rest: '' }
}
